/* eslint-disable react/jsx-curly-brace-presence */
import React, { Fragment } from 'react';
import { bool, string } from 'prop-types';
import classnames from 'classnames';
import { priceShape } from 'shapes';
import { FormattedPrice } from 'components';
import { FormattedMessage } from 'react-intl';
import {
    PerUnit,
    Discount,
    VATExcludedLabel,
    VATLabel,
    VATTooltip,
} from './components';

export default function Price({
    price,
    isVATExcluded,
    showVATForB2B,
    isPerUnit,
    className,
    locale,
    currency,
    showFromPrice,
    showDiscount,
    showRetailPrice,
}) {
    const integerVat =
        price.actual_price && price.actual_price.with_vat.integer;
    const decimalVat =
        price.actual_price && price.actual_price.with_vat.decimal;
    const integerVATExcluded =
        price.actual_price && price.actual_price.without_vat
            ? price.actual_price.without_vat.integer
            : null;
    const decimalVATExcluded =
        price.actual_price && price.actual_price.without_vat
            ? price.actual_price.without_vat.decimal
            : null;
    const perUnitClassNames = classnames('prices__per-unit', {
        [`${className}__per-unit`]: className,
    });
    const pricesClassNames = classnames('prices__price', {
        [`${className}__price`]: className,
        [`${className}__price--pro-vat`]: showVATForB2B,
    });
    const complementaryClassName = classnames('prices__complementary-price', {
        [`${className}__complementary-price`]: className,
    });

    return (
        <div className={className}>
            <div className="prices__from-and-retail-wrapper">
                {showFromPrice && (
                    <p className="prices__from">
                        <FormattedMessage
                            id="listing.price.fromPrice"
                            defaultMessage="From"
                            description="From price label of price component"
                        />
                    </p>
                )}

                {showRetailPrice && !!price.retail_price && (
                    <p className="prices__retail-price">
                        <FormattedPrice
                            integer={
                                price.retail_price[
                                    showVATForB2B || !isVATExcluded
                                        ? 'with_vat'
                                        : 'without_vat'
                                ].integer
                            }
                            decimal={
                                price.retail_price[
                                    showVATForB2B || !isVATExcluded
                                        ? 'with_vat'
                                        : 'without_vat'
                                ].decimal
                            }
                            locale={locale}
                            currency={currency}
                        />

                        {isVATExcluded && <VATExcludedLabel locale={locale} />}
                    </p>
                )}
            </div>

            {isPerUnit ? (
                <div className={perUnitClassNames}>
                    {isVATExcluded && !showVATForB2B && (
                        <span className="prices__per-unit__vat-excluded">
                            <FormattedMessage
                                id="listing.price.perUnit"
                                defaultMessage="or {price} by unit {vat}"
                                description="Price by unit label of price component"
                                values={{
                                    price: (
                                        <FormattedPrice
                                            integer={
                                                showVATForB2B
                                                    ? integerVat
                                                    : integerVATExcluded
                                            }
                                            decimal={
                                                showVATForB2B
                                                    ? decimalVat
                                                    : decimalVATExcluded
                                            }
                                            locale={locale}
                                            currency={currency}
                                        />
                                    ),
                                    vat: showVATForB2B ? (
                                        <VATLabel />
                                    ) : (
                                        <VATExcludedLabel locale={locale} />
                                    ),
                                }}
                            />
                        </span>
                    )}

                    <FormattedMessage
                        id="listing.price.perUnit"
                        defaultMessage="or {price} by unit {vat}"
                        description="Price by unit label of price component"
                        values={{
                            price: (
                                <FormattedPrice
                                    integer={integerVat}
                                    decimal={decimalVat}
                                    locale={locale}
                                    currency={currency}
                                />
                            ),
                            vat: isVATExcluded ? <VATLabel /> : '',
                        }}
                    />
                </div>
            ) : (
                <Fragment>
                    <div className={pricesClassNames}>
                        <FormattedPrice
                            integer={
                                showVATForB2B || !isVATExcluded
                                    ? integerVat
                                    : integerVATExcluded
                            }
                            decimal={
                                showVATForB2B || !isVATExcluded
                                    ? decimalVat
                                    : decimalVATExcluded
                            }
                            locale={locale}
                            currency={currency}
                        />
                        {price.unit && <PerUnit value={price.unit} />}
                        {showVATForB2B && (
                            <Fragment>
                                <VATLabel />
                                <VATTooltip
                                    className="prices__vat-tooltip"
                                    locale={locale}
                                />
                            </Fragment>
                        )}
                        {isVATExcluded && !showVATForB2B && (
                            <VATExcludedLabel locale={locale} />
                        )}
                        {showDiscount && !!price.discount && (
                            <Discount value={price.discount} />
                        )}
                    </div>

                    {isVATExcluded && !showVATForB2B && (
                        <div className={complementaryClassName}>
                            <FormattedPrice
                                integer={integerVat}
                                decimal={decimalVat}
                                locale={locale}
                                currency={currency}
                            />
                            {price.unit && <PerUnit value={price.unit} />}
                            <VATLabel />
                        </div>
                    )}
                </Fragment>
            )}
        </div>
    );
}

Price.propTypes = {
    price: priceShape.isRequired,
    className: string,
    isVATExcluded: bool,
    showVATForB2B: bool,
    isPerUnit: bool,
    showFromPrice: bool,
    showDiscount: bool,
    showRetailPrice: bool,
    locale: string.isRequired,
    currency: string.isRequired,
};

Price.defaultProps = {
    isVATExcluded: false,
    showVATForB2B: false,
    isPerUnit: false,
    className: '',
    showFromPrice: false,
    showDiscount: false,
    showRetailPrice: false,
};
